/**
 * Эрхийн КАТАЛОГ — цэвэр өгөгдөл, I/O байхгүй.
 *
 * ЗАРЧИМ
 *  Эрх нь өмнө нь КОД байсан: `requireRole('WAREHOUSE','ADMIN')` гэж 41
 *  газар бичигдсэн, сайдбарт `roles: [...]` гэж давхардсан. Админ
 *  өөрчлөх боломжгүй, ямар эрх байдгийг нэг дор харах ч арга байгаагүй.
 *
 *  Одоо энэ файл нь ЦОРЫН ГАНЦ жагсаалт:
 *   · ямар модуль (= хуудас) байдаг
 *   · модуль бүрд ямар үйлдэл утгатай
 *   · үйлдэл бүр АНХДАГЧААР ямар роль дээр асаалттай байх
 *
 *  Сервер (`requirePermission`), веб (цэс, товч), гар утас (аль апп)
 *  гурвуулаа ҮҮНЭЭС уншина — тиймээс гурав хоорондоо зөрөх боломжгүй.
 *
 * ⚠️ Энд байгаа анхдагч утгууд нь өнөөдрийн `requireRole`-уудаас ЯГ
 *    ХУУЛАГДСАН. Өөрчлөх нь эрхийн өөрчлөлт — санамсаргүй засахгүй.
 *    Зөрүү гарсан цөөн тохиолдлыг `hint`-д тэмдэглэв.
 *
 * ⚠️ Админ DB-д зөвхөн ЗӨРҮҮГ (override) хадгална, бүтэн жагсаалтыг
 *    биш. Ингэснээр шинэ эрх нэмэгдэхэд хуучин тохиргоо чимээгүй
 *    «хаалттай» болохгүй — код дахь анхдагчаа авна.
 */
import { APP_DEVICE_TYPES } from './auth.js';
// ------------------------------------------------------------
//  Роль
//
//  ⚠️ `packages/schema`-гийн `UserRole` enum-тэй ижил байх ЁСТОЙ.
//     domain нь schema-аас хамаардаггүй (мөчлөг үүсэхээс сэргийлж)
//     тул жагсаалт энд давтагдана — тест нь хоёрыг тулгана.
// ------------------------------------------------------------
export const PERM_ROLES = [
    'ADMIN',
    'DIRECTOR_READONLY',
    'WAREHOUSE',
    'STORE_MANAGER',
    'SALESPERSON',
    'COLLECTOR',
    'ACCOUNTANT',
];
const ALL_ROLES = PERM_ROLES;
/**
 * Эрх нь бүрэн, засагдахгүй роль. Админ өөрийгөө системээс түгжиж
 * ГАРГАХ БОЛОМЖГҮЙ байх ёстой — эс бөгөөс сэргээх зам үлдэхгүй.
 */
export function isLockedRole(role) {
    return role === 'ADMIN';
}
/**
 * Зөвхөн уншина. `readOnlyGuard` (core) нь эрхээс ДЭЭГҮҮР бичих бүх
 * үйлдлийг хаадаг тул энэ ролийн бичих эрхийг асаах нь утгагүй —
 * UI-д ч түгжигдэнэ.
 */
export function isReadOnlyRole(role) {
    return role === 'DIRECTOR_READONLY';
}
// ------------------------------------------------------------
//  Үйлдэл
//
//  Дөрвөн ерөнхий үйлдэл (view/create/edit/delete) дээр нэмээд
//  салбарын тодорхой үйлдлүүд. Бүгдийг «засах» болгож нэгтгэвэл
//  өнөөгийн зан өөрчлөгдөнө: жишээ нь агуулах шошго хэвлэдэг ч үнэ
//  тавьдаггүй — хоёр өөр эрх.
// ------------------------------------------------------------
export const PERM_ACTION_LABEL = {
    view: 'Харах',
    create: 'Үүсгэх',
    edit: 'Засах',
    delete: 'Устгах',
    sell: 'Зарах',
    void: 'Цуцлах',
    refund: 'Буцаалт бүртгэх',
    price: 'Үнэ тавих',
    move: 'Зөөх',
    label: 'Шошго, серийн дугаар',
    writeOff: 'Актлах',
    photo: 'Зураг оруулах',
    photoEdit: 'Зураг засах, устгах',
    opening: 'Эхний үлдэгдэл бүртгэх',
    defect: 'Гэмтэл бүртгэх',
    scan: 'Сканнердах',
    receive: 'Хүлээж авах',
    manage: 'Нээх, хаах',
    resolve: 'Шийдвэрлэх',
    location: 'Байршил засах',
    model: 'Загвар засах',
    fx: 'Ханш оруулах',
    device: 'Төхөөрөмж удирдах',
    permission: 'Эрх тохируулах',
    use: 'Ашиглах',
    pay: 'Төлбөр бүртгэх',
};
/** Бичих үйлдэл эсэх — `DIRECTOR_READONLY`-д түгжигдэнэ. */
export function isWriteAction(action) {
    return action !== 'view';
}
// ------------------------------------------------------------
//  Бүлэг — сайдбарын дараалалтай ижил
// ------------------------------------------------------------
/**
 * ⚠️ САЙДБАРЫН БҮЛЭГТЭЙ ЯГ ИЖИЛ байх ёстой
 *    (`apps/web/src/components/app-shell.tsx` → `navGroups`).
 *
 *    Админ «Агуулахын ажилтан юу харах вэ» гэж тохируулахдаа өөрийн
 *    харж заншсан цэсийг хайна. Эрхийн хуудас өөр нэрээр, өөр
 *    дарааллаар бүлэглэвэл хоёрыг толгойдоо буулгах ажил админд
 *    үлдэнэ. Сайдбарыг өөрчлөх бол ЭНДЭХИЙГ Ч хамт өөрчилнө.
 */
export const PERM_GROUP_LABEL = {
    stats: 'СТАТИСТИК',
    accounting: 'НЯГТЛАН',
    goods: 'ГУТАЛ',
    supply: 'НИЙЛҮҮЛЭЛТ',
    warehouse: 'АГУУЛАХ',
    branch: 'САЛБАР',
    ecommerce: 'ЦАХИМ ХУДАЛДАА',
    mobile: 'ГАР УТАСНЫ АПП',
    setup: 'СИСТЕМИЙН ТОХИРГОО',
};
/**
 * Бүлгийн ЭРЭМБЭ — дэлгэц энэ дарааллаар зурна.
 *
 * ⚠️ `PERM_MODULES`-ийн дараалалаас БИШ эндээс гарна: каталогт шинэ
 *    модуль нэмэхэд массивын хаана байрлуулснаас болж бүлгийн
 *    дараалал үсрэх ёсгүй.
 */
export const PERM_GROUPS = Object.keys(PERM_GROUP_LABEL);
/**
 * Өртөг харах эрхтэй роль — ЦОРЫН ГАНЦ жагсаалт. Сервер (`maskCost`),
 * веб (`•••`) хоёулаа үүнийг уншина.
 *
 * ⚠️ SALESPERSON, COLLECTOR өртөг харахгүй (CLAUDE.md). Нуулт нь СЕРВЕРТ
 *    хийгдэнэ — зөвхөн UI-д нуувал DevTools-оор харагдана.
 *
 * ⚠️ STORE_MANAGER 2026-09-25-наас ХАСАГДСАН (эзэмшигчийн шийдвэр):
 *    салбарын менежер зарах үнэ л хардаг, худалдан авалтын өртөг,
 *    ашгийн марж хардаггүй. Тиймээс `finance`, `reports` хуудас ч
 *    хаагдав — тэдгээр нь ЭНЭ жагсаалтыг дагадаг.
 */
export const COST_ROLES = ['ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE', 'ACCOUNTANT'];
export function canSeeCost(role) {
    return COST_ROLES.includes(role);
}
export const PERM_MODULES = [
    // ─────────────────────────────────── СТАТИСТИК · АГУУЛАХ · САЛБАР
    {
        key: 'dashboard',
        label: 'Хяналтын самбар',
        path: '/',
        group: 'stats',
        // ⚠️ САЛБАРЫН ХОЁР РОЛЬ ХАСАГДСАН (эзэмшигчийн шийдвэр 2026-09-27):
        //    компанийн нийт орлого, бүх нөөц, статистик нь салбарын ажилтанд
        //    хамаагүй — тэд `/`-д өөрийн салбарын самбарыг (`branchDashboard`)
        //    хардаг. Энэ эрх `/reports/*`-ийн компанийн тайланг ч хаадаг тул
        //    цэснээс нуухаас гадна API-аас ч хаагдана.
        actions: [
            {
                action: 'view',
                roles: ['ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE', 'COLLECTOR'],
                hint: 'Компанийн самбар, статистик. Салбарын самбараас давамгайлна',
            },
        ],
    },
    {
        key: 'branchDashboard',
        label: 'Салбарын самбар',
        path: '/',
        group: 'branch',
        actions: [
            {
                action: 'view',
                roles: ['STORE_MANAGER', 'SALESPERSON'],
                hint: 'Өөрийн салбарын размерын нөөц, орлого, агуулахаас татсан гутал',
            },
        ],
    },
    {
        key: 'scan',
        label: 'Серийн дугаараар хайх',
        // ⚠️ Тусдаа «Скан» ХУУДАС БАЙХГҮЙ болов — уншуулах талбар нь
        //    «Бүх гутал» дээр шилжсэн. Эрх нь ХЭВЭЭР:
        //    `GET /items/by-serial/:serial`-ыг энэ хаана.
        path: null,
        group: 'goods',
        actions: [
            { action: 'view', roles: ALL_ROLES, hint: 'Баркод уншуулж гутал олох' },
        ],
    },
    {
        key: 'sales',
        label: 'Касс',
        path: '/sales',
        group: 'branch',
        actions: [
            { action: 'view', roles: ['ADMIN', 'STORE_MANAGER', 'SALESPERSON'] },
            { action: 'sell', roles: ['ADMIN', 'STORE_MANAGER', 'SALESPERSON'], hint: 'Захиалга баталгаажуулах' },
        ],
    },
    {
        key: 'customers',
        label: 'Худалдан авагчийн бүртгэл',
        path: null,
        group: 'branch',
        actions: [
            { action: 'view', roles: ['ADMIN', 'DIRECTOR_READONLY', 'STORE_MANAGER', 'SALESPERSON'] },
            { action: 'edit', roles: ['ADMIN', 'STORE_MANAGER', 'SALESPERSON'] },
            { action: 'delete', roles: ['ADMIN', 'STORE_MANAGER', 'SALESPERSON'] },
        ],
    },
    {
        key: 'orders',
        label: 'Захиалга, борлуулалт',
        path: '/orders',
        group: 'branch',
        actions: [
            { action: 'view', roles: ['ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE', 'STORE_MANAGER', 'SALESPERSON', 'ACCOUNTANT'] },
            { action: 'void', roles: ['ADMIN', 'STORE_MANAGER'], hint: 'Захиалга VOID — мөр устдаггүй' },
            { action: 'refund', roles: ['ADMIN', 'STORE_MANAGER', 'SALESPERSON'] },
        ],
    },
    {
        // Хувь зээл (2026-09-28) — салбарын ажилтан ӨӨРИЙН салбарынхаа зээлийг
        // хөтөлнө (`canActForStore`). Өртөг байхгүй.
        key: 'installments',
        label: 'Хувь зээл',
        path: '/installments',
        group: 'branch',
        actions: [
            {
                action: 'view',
                roles: ['ADMIN', 'DIRECTOR_READONLY', 'STORE_MANAGER', 'SALESPERSON'],
                hint: 'Хувь зээлтэй худалдан авагч, хуваарь, үлдэгдэл',
            },
            {
                action: 'pay',
                roles: ['ADMIN', 'STORE_MANAGER', 'SALESPERSON'],
                hint: 'Сарын төлөлт бүртгэх, бүрэн төлөгдсөн зээлийг хаах',
            },
            { action: 'void', roles: ['ADMIN', 'STORE_MANAGER'], hint: 'Алдаатай төлөлтийг хүчингүй болгох' },
        ],
    },
    // ─────────────────────────────────── НИЙЛҮҮЛЭЛТ · АГУУЛАХ
    {
        key: 'purchasing',
        label: 'Нийлүүлэлт · Орос',
        path: '/purchasing',
        group: 'supply',
        actions: [
            { action: 'view', roles: ['ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE', 'ACCOUNTANT'] },
            { action: 'create', roles: ['ADMIN', 'WAREHOUSE', 'COLLECTOR'], hint: 'Аялал нээх, дуусгах' },
            { action: 'edit', roles: ['ADMIN', 'WAREHOUSE'], hint: 'Тээвэр, гааль, өртөг хуваарилалт' },
            {
                action: 'defect',
                roles: ['ADMIN', 'WAREHOUSE'],
                hint: 'Хайрцаг задлахад илэрсэн гэмтэл — цуглуулагчийн оноог бууруулна',
            },
        ],
    },
    {
        key: 'items',
        label: 'Бүх гутал',
        path: '/items',
        group: 'goods',
        actions: [
            // ⚠️ STORE_MANAGER энд БАЙХГҮЙ (2026-09-25): салбарын менежер
            //    агуулахын бараа биш, өөрийн салбарын борлуулалтыг хардаг.
            //    Баркод уншуулж гутал олох нь `scan.view`-ээр хэвээр —
            //    касс, баталгаа хоёр түүгээр ажиллана.
            { action: 'view', roles: ['ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE', 'SALESPERSON', 'COLLECTOR', 'ACCOUNTANT'] },
            { action: 'create', roles: ['ADMIN', 'WAREHOUSE', 'COLLECTOR'], hint: 'Гутал бүртгэх (багц ба ганцаар)' },
            { action: 'price', roles: ['ADMIN', 'WAREHOUSE'] },
            { action: 'move', roles: ['ADMIN', 'WAREHOUSE'] },
            { action: 'label', roles: ['ADMIN', 'WAREHOUSE'], hint: 'CLAUDE.md №6 — серийн дугаар агуулахад олгогдоно' },
            { action: 'writeOff', roles: ['ADMIN'] },
            {
                action: 'photo',
                roles: ['ADMIN', 'WAREHOUSE', 'STORE_MANAGER', 'SALESPERSON', 'COLLECTOR'],
                hint: 'Зураг оруулах — баталгааны нотолгоо',
            },
            {
                action: 'photoEdit',
                roles: ['ADMIN', 'WAREHOUSE'],
                hint: 'Зураг солих, устгах. Нотолгоо тул нарийн эрх',
            },
            {
                action: 'opening',
                roles: ['ADMIN', 'WAREHOUSE', 'STORE_MANAGER', 'SALESPERSON'],
                hint: 'Шошготой байсан гутлыг POS-оор нэг удаа бүртгэх. Бүртгэл дуусмагц хаана',
            },
        ],
    },
    {
        key: 'transfers',
        label: 'Шилжүүлэг',
        path: '/transfers',
        group: 'warehouse',
        actions: [
            // ⚠️ Менежер ВЭБИЙН шилжүүлгийн хуудсыг харахгүй (2026-09-25), гэхдээ
            //    `scan` + `receive` нь ХЭВЭЭР: салбар нь ирсэн барааг гар
            //    утсаар уншуулж (`features/store/receive.tsx`), эсвэл вэбийн
            //    «Хүлээн авалт» (`/receiving`, 2026-10-03) хуудсаар уншуулалгүй
            //    хүлээн авдаг. Замд яваа жагсаалт нь `scan`-аар ч нээгддэг.
            { action: 'view', roles: ['ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE', 'SALESPERSON', 'COLLECTOR'] },
            { action: 'create', roles: ['ADMIN', 'WAREHOUSE'], hint: 'Илгээх, мөр нэмэх, цуцлах' },
            { action: 'scan', roles: ['ADMIN', 'WAREHOUSE', 'STORE_MANAGER', 'SALESPERSON'] },
            {
                action: 'receive',
                roles: ['ADMIN', 'WAREHOUSE', 'STORE_MANAGER'],
                hint: 'Вэбийн «Хүлээн авалт» хуудас (САЛБАР цэс) ба гар утсаар хүлээн авах. Өөрийн салбарынхыг л',
            },
        ],
    },
    {
        key: 'stockCounts',
        label: 'Тооллого',
        path: '/stock-counts',
        group: 'warehouse',
        actions: [
            { action: 'view', roles: ['ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE'] },
            { action: 'manage', roles: ['ADMIN', 'WAREHOUSE'], hint: 'Тооллого нээх, хаах' },
            { action: 'scan', roles: ['ADMIN', 'WAREHOUSE', 'STORE_MANAGER', 'SALESPERSON'] },
            { action: 'delete', roles: ['ADMIN'], hint: 'Тооллогыг устгах (гутал хөндөгдөхгүй)' },
        ],
    },
    {
        key: 'wholesale',
        label: 'Бөөний худалдаа',
        path: '/wholesale',
        group: 'ecommerce',
        actions: [{ action: 'view', roles: ['ADMIN'] }],
    },
    {
        key: 'warranty',
        label: 'Баталгаа',
        path: '/warranty',
        group: 'branch',
        actions: [
            { action: 'view', roles: ['ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE', 'STORE_MANAGER', 'SALESPERSON'] },
            { action: 'create', roles: ['ADMIN', 'WAREHOUSE', 'STORE_MANAGER', 'SALESPERSON'], hint: 'Нэхэмжлэл бүртгэх' },
            { action: 'resolve', roles: ['ADMIN', 'STORE_MANAGER'] },
        ],
    },
    // ─────────────────────────────────── ЦАХИМ ХУДАЛДАА · СТАТИСТИК · САЛБАР
    {
        key: 'finance',
        label: 'Санхүү, нэхэмжлэх',
        path: '/finance',
        group: 'stats',
        actions: [{ action: 'view', roles: COST_ROLES }],
    },
    {
        key: 'credit',
        label: 'Нийлүүлэгчийн зээл',
        path: '/credit',
        group: 'supply',
        actions: [
            { action: 'view', roles: COST_ROLES, hint: 'Авсан зээл, төлөлтийн түүх' },
            {
                action: 'pay',
                roles: ['ADMIN', 'ACCOUNTANT'],
                hint: 'Төлбөр бүртгэх, цуцлах. Мөнгөний бүртгэл — админ, нягтлан (2026-10-15)',
            },
        ],
    },
    {
        key: 'reports',
        label: 'Тайлан',
        path: '/reports',
        group: 'stats',
        actions: [{ action: 'view', roles: COST_ROLES }],
    },
    {
        key: 'calendar',
        label: 'Хуанли',
        path: '/calendar',
        group: 'stats',
        // Компанийн нийт орлого харуулна — салбарын ажилтанд хаалттай
        // (`dashboard.view`-ийн 2026-09-27 шийдвэртэй ижил). Өртөг байхгүй.
        actions: [
            {
                action: 'view',
                roles: ['ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE', 'ACCOUNTANT'],
                hint: 'Өдөр бүрийн орлого, зарагдсан болон агуулахад орж ирсэн гутал',
            },
        ],
    },
    {
        // Нягтлан бодогчийн хэсэг (2026-10-15): өдрийн борлуулалт салбар ×
        // нийлүүлэгч, үнийн өөрчлөлт, нийлүүлэгчийн тооцоо, ханшийн тэгшитгэл,
        // размерын үлдэгдэл. Бүгд УНШИНА — өртөг агуулна.
        key: 'accounting',
        label: 'Нягтлан',
        path: '/accounting',
        group: 'accounting',
        actions: [
            {
                action: 'view',
                roles: ['ADMIN', 'DIRECTOR_READONLY', 'ACCOUNTANT'],
                hint: 'Борлуулалт, үнийн өөрчлөлт, нийлүүлэгчийн тооцоо, ханш, үлдэгдэл',
            },
        ],
    },
    {
        key: 'branches',
        label: 'Салбар',
        path: '/branches',
        group: 'branch',
        // ⚠️ `COST_ROLES`-ыг дагахаа больсон: менежер өөрийн салбарын
        //    тоймыг хардаг ч өртөг нь `maskCost`-оор нууцлагдана.
        actions: [
            { action: 'view', roles: ['ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE', 'STORE_MANAGER'] },
        ],
    },
    // ─────────────────────────────────── СИСТЕМИЙН ТОХИРГОО
    {
        key: 'setupSuppliers',
        label: 'Нийлүүлэгч',
        path: '/setup/suppliers',
        group: 'supply',
        actions: [
            { action: 'view', roles: ['ADMIN', 'WAREHOUSE'] },
            { action: 'edit', roles: ['ADMIN', 'WAREHOUSE'], hint: 'Үүсгэх, засах, устгах' },
        ],
    },
    {
        key: 'setupStores',
        label: 'Салбар, агуулах',
        path: '/setup/stores',
        group: 'setup',
        actions: [
            { action: 'view', roles: ['ADMIN', 'WAREHOUSE'] },
            { action: 'edit', roles: ['ADMIN'], hint: 'Салбар үүсгэх, засах' },
            { action: 'location', roles: ['ADMIN', 'WAREHOUSE'] },
        ],
    },
    {
        key: 'setupStaff',
        label: 'Ажилтан',
        path: '/setup/staff',
        group: 'setup',
        actions: [
            { action: 'view', roles: ['ADMIN'] },
            { action: 'edit', roles: ['ADMIN'], hint: 'Хэрэглэгч үүсгэх, засах, нууц үг, TOTP' },
            { action: 'device', roles: ['ADMIN'], hint: 'Төхөөрөмж хаах, сесс салгах' },
            { action: 'permission', roles: ['ADMIN'], hint: 'ЭНЭ хуудас — эрхийн матриц' },
        ],
    },
    {
        key: 'setupReference',
        label: 'Жагсаалт, ангилал',
        path: '/setup/reference',
        group: 'setup',
        actions: [
            { action: 'view', roles: ['ADMIN', 'WAREHOUSE'] },
            { action: 'edit', roles: ['ADMIN'], hint: 'Зээлийн апп, шалтгаан, өнгө, баталгааны нөхцөл' },
            { action: 'model', roles: ['ADMIN', 'WAREHOUSE'] },
            { action: 'fx', roles: ['ADMIN', 'WAREHOUSE'] },
        ],
    },
    {
        key: 'setupSystem',
        label: 'Систем',
        path: '/setup/system',
        group: 'setup',
        actions: [
            { action: 'view', roles: ['ADMIN'] },
            { action: 'manage', roles: ['ADMIN'], hint: 'Backup-ийн төлөв, хэрэглэгчийн эрх, аудит' },
        ],
    },
    // ─────────────────────────────────── ГАР УТАСНЫ АПП
    //
    //  ⚠️ Энэ ГУРАВ нь ХУУДАС биш. `appModeFromPermissions` эдгээрийг
    //     уншиж нэвтрэлтийн үед аль горимд оруулахыг шийднэ. Хэд нь ч
    //     асаалттай бол АГУУЛАХ давамгайлна — тэр горим нь илүү өргөн
    //     (скан + бүртгэл), мөн хуучин `resolveAppMode`-ийн зан ийм
    //     байсан (ADMIN цуглуулагчийн утсан дээр ч агуулахын горимд).
    //
    //  Нэг ажилтан агуулах · цуглуулагч · салбар ГУРВЫН НЭГ нь байна.
    //  Хоёрыг зэрэг өгвөл алдаа заахгүй, дээрх эрэмбээр нэг нь сонгогдоно.
    {
        key: 'mobileCollector',
        label: 'Цуглуулагчийн апп',
        path: null,
        group: 'mobile',
        actions: [
            { action: 'use', roles: ['COLLECTOR'], hint: 'Орост офлайн цуглуулах горим' },
        ],
    },
    {
        key: 'mobileWarehouse',
        label: 'Агуулахын апп',
        path: null,
        group: 'mobile',
        actions: [
            { action: 'use', roles: ['ADMIN', 'WAREHOUSE'], hint: 'Sunmi гар төхөөрөмжийн горим' },
        ],
    },
    {
        key: 'mobileStore',
        label: 'Салбарын апп',
        path: null,
        group: 'mobile',
        actions: [
            {
                action: 'use',
                roles: ['STORE_MANAGER', 'SALESPERSON'],
                hint: 'Уншуулж зарах горим (POS)',
            },
        ],
    },
];
// ------------------------------------------------------------
//  Гаргаж авсан хэлбэрүүд
// ------------------------------------------------------------
/** `purchasing` + `view` → `purchasing.view` */
export function permKey(module, action) {
    return `${module}.${action}`;
}
/** Каталогт байгаа БҮХ эрх — тохиргоо шалгахад. */
export const ALL_PERMISSIONS = PERM_MODULES.flatMap((m) => m.actions.map((a) => permKey(m.key, a.action)));
const ALL_PERMISSION_SET = new Set(ALL_PERMISSIONS);
/** Каталогт байгаа эрх мөн эсэх — хуучирсан түлхүүр DB-д үлдэж болно. */
export function isKnownPermission(key) {
    return ALL_PERMISSION_SET.has(key);
}
/** Роль бүрийн АНХДАГЧ эрх — код дахь тогтмол. */
export const DEFAULT_GRANTS = (() => {
    const out = {};
    for (const role of PERM_ROLES) {
        out[role] = PERM_MODULES.flatMap((m) => m.actions.filter((a) => a.roles.includes(role)).map((a) => permKey(m.key, a.action)));
    }
    return out;
})();
/**
 * Бодит эрх = анхдагч + админы зөрүү.
 *
 * @param role      хэрэглэгчийн роль
 * @param overrides DB-д хадгалагдсан зөрүү (`{ 'items.price': false }`)
 *
 * ⚠️ Хоёр дүрэм эрхээс ДЭЭГҮҮР:
 *    · ADMIN үргэлж бүгд — өөрийгөө түгжихээс сэргийлнэ
 *    · DIRECTOR_READONLY бичих үйлдэлгүй — `readOnlyGuard`-тай нийцнэ
 */
export function effectivePermissions(role, overrides = {}) {
    if (isLockedRole(role))
        return new Set(ALL_PERMISSIONS);
    const defaults = DEFAULT_GRANTS[role] ?? [];
    const out = new Set(defaults);
    for (const [key, allowed] of Object.entries(overrides)) {
        if (!isKnownPermission(key))
            continue;
        if (allowed)
            out.add(key);
        else
            out.delete(key);
    }
    if (isReadOnlyRole(role)) {
        for (const key of [...out]) {
            const action = key.slice(key.indexOf('.') + 1);
            if (isWriteAction(action))
                out.delete(key);
        }
    }
    return out;
}
/**
 * Гар утасны горим — эрхээс гарна.
 *
 * Өмнө нь роль дээр хатуу бичигдсэн байв (`COLLECTOR → collector`).
 * Одоо админ шийднэ: жишээ нь салбарын менежерт агуулахын аппыг
 * нээж өгч болно.
 *
 * ⚠️ Төхөөрөмжийн төрөл нь хэвээр хатуу шалгалт — WEB, LABEL_PRINTER
 *    дээр апп ажиллахгүй, энэ нь эрхийн асуудал биш.
 */
export function appModeFromPermissions(permissions, deviceType) {
    if (!APP_DEVICE_TYPES.includes(deviceType))
        return null;
    if (permissions.has('mobileWarehouse.use'))
        return 'warehouse';
    if (permissions.has('mobileCollector.use'))
        return 'collector';
    if (permissions.has('mobileStore.use'))
        return 'store';
    return null;
}
/**
 * Шинэ төхөөрөмжийн мөр ямар ТӨРЛӨӨР үүсэх вэ.
 *
 * Ажилтан төрлөө СОНГОХГҮЙ — сонгуулбал цуглуулагч өөрийгөө агуулахын
 * горимд оруулах гарц үүснэ. Хуучин `deviceTypeForRole` нь үүнийг
 * ролиос гаргадаг байсан; одоо эрхээс гарна, эс бөгөөс админ аппыг
 * нээж өгсөн ч төхөөрөмжийн мөр үүсэхгүй тул нэвтрэлт бүтэхгүй.
 *
 * ⚠️ Эрэмбэ нь `appModeFromPermissions`-той ИЖИЛ байх ёстой — үгүй бол
 *    HANDHELD дээр цуглуулагчийн горим гарах мэт зөрүү үүснэ.
 */
export function deviceTypeFromPermissions(permissions) {
    if (permissions.has('mobileWarehouse.use'))
        return 'HANDHELD';
    if (permissions.has('mobileCollector.use'))
        return 'COLLECTOR_PHONE';
    if (permissions.has('mobileStore.use'))
        return 'POS';
    return null;
}
//# sourceMappingURL=permissions.js.map